import { Package } from '@phosphor-icons/react';
import { LayoutGroup } from 'framer-motion';
import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';

import {
  groupProductVariants,
  useGetCategoriesQuery,
  useGetProductsQuery,
  type ProductDto,
  type ProductListing,
  type ProductVariant,
} from '@entities/product';
import { useGetShopServicesQuery } from '@entities/shopService';
import { ServicesPage } from '@pages/ServicesPage';
import { useGetGiveawayQuery } from '@entities/giveaway';
import { useGetFlashDrawQuery } from '@entities/flashDraw';
import { ProductSheet } from '@features/buy-product';
import {
  ConfettiCelebration,
  useStorefrontCelebration,
} from '@features/storefront-celebration';
import { EmptyState } from '@shared/ui/EmptyState';
import { Screen } from '@shared/ui/Screen';
import { SearchField } from '@shared/ui/SearchField';
import { Spinner } from '@shared/ui/Spinner';
import { ProductGrid } from '@widgets/ProductGrid';
import { FlashDealHero } from '@widgets/FlashDealHero';
import { FlashDrawHero } from '@widgets/FlashDrawHero';
import { GiveawayHero } from '@widgets/GiveawayHero';
import { SupportFab } from '@widgets/SupportFab';
import { StoreSearchOverlay } from './StoreSearchOverlay';
import { StoreSectionSwitcher } from './StoreSectionSwitcher';

export function StorePage() {
  const celebration = useStorefrontCelebration();
  const { data: catalog, isLoading, isError, refetch } = useGetProductsQuery(undefined, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { data: giveawayResponse, refetch: refetchGiveaway } = useGetGiveawayQuery(undefined, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
    pollingInterval: 15_000,
  });
  const { data: flashDrawResponse, refetch: refetchFlashDraw } = useGetFlashDrawQuery(undefined, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
    pollingInterval: 5_000,
  });
  const products = catalog?.products ?? [];
  const listings = useMemo(() => groupProductVariants(products), [products]);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedVariantIds, setSelectedVariantIds] = useState<string[]>([]);
  // Set when the customer came in through a card's Buy button rather than the
  // card itself: the sheet then opens on its confirmation step.
  const [buyNow, setBuyNow] = useState(false);

  // The field updates on every keystroke; re-filtering and re-rendering the
  // grid is allowed to lag a frame behind it. Without this the typing itself
  // was competing with a full grid re-render on the same tick.
  const deferredQuery = useDeferredValue(query);

  // Null is the "All" tab, which is also the whole storefront when the
  // operator has made no sections — the shop's behaviour before they existed.
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const { data: categories = [] } = useGetCategoriesQuery();

  // Features shown as product cards — SMS today, more later. They live in
  // their own tab rather than the grid because they are not catalogue rows,
  // and the server hides any the customer may not open, so an empty list is
  // simply a tab that does not appear.
  const { data: shopServices = [] } = useGetShopServicesQuery();
  const servicesOffered = shopServices.length > 0;
  const [showNumbers, setShowNumbers] = useState(false);

  // A section the operator hid or emptied while the tab was open would
  // otherwise leave the grid stuck on a filter nothing can satisfy.
  useEffect(() => {
    if (categoryId && !categories.some((category) => category.id === categoryId)) {
      setCategoryId(null);
    }
  }, [categories, categoryId]);

  const visible = useMemo(() => {
    const needle = deferredQuery.trim().toLowerCase();
    // Search deliberately ignores the active tab: someone typing a product
    // name wants it found, not filtered out by a section they forgot is on.
    if (needle) {
      return listings.filter(
        (listing) =>
          listing.name.toLowerCase().includes(needle) ||
          listing.variants.some((variant) => variant.product.name.toLowerCase().includes(needle)),
      );
    }
    // The full-screen search is a catalogue-wide surface even before the
    // first character is entered; its heading says "All products" and should
    // not inherit a hidden section filter from the page underneath.
    if (searchOpen) return listings;
    if (!categoryId) return listings;

    return listings.filter((listing) => listing.primary.category?.id === categoryId);
  }, [listings, deferredQuery, categoryId, searchOpen]);

  const selected = useMemo(
    () => products.find((product) => product.id === selectedId) ?? null,
    [products, selectedId],
  );
  const selectedVariants = useMemo(
    () => {
      const variantsById = new Map<string, ProductVariant>();
      for (const listing of listings) {
        for (const variant of listing.variants) variantsById.set(variant.product.id, variant);
      }

      return selectedVariantIds
        .map((id) => variantsById.get(id))
        .filter((variant): variant is ProductVariant => Boolean(variant));
    },
    [listings, selectedVariantIds],
  );
  const selectedListingName = useMemo(
    () => {
      if (selectedVariantIds.length <= 1) return selected?.name ?? null;

      return (
        listings.find((listing) =>
          listing.variants.some((variant) => selectedVariantIds.includes(variant.product.id)),
        )?.name ?? selected?.name ?? null
      );
    },
    [listings, selected, selectedVariantIds],
  );
  const featured = useMemo(
    () => products.find((product) => product.id === catalog?.featuredFlashProductId) ?? null,
    [products, catalog?.featuredFlashProductId],
  );
  const clockOffsetMs = catalog
    ? Date.parse(catalog.serverTime) - catalog.clientReceivedAt
    : 0;
  const giveawayClockOffsetMs = giveawayResponse
    ? Date.parse(giveawayResponse.serverTime) - giveawayResponse.clientReceivedAt
    : 0;
  const flashDrawClockOffsetMs = flashDrawResponse
    ? Date.parse(flashDrawResponse.serverTime) - flashDrawResponse.clientReceivedAt
    : 0;

  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  // The server does not need a cleanup job, but an open catalogue needs one
  // refresh at the first local expiry so its displayed price catches up.
  useEffect(() => {
    const nextEnd = products.reduce<number | null>((nearest, product) => {
      if (!product.flashDeal) return nearest;
      const end = Date.parse(product.flashDeal.endsAt);
      return nearest === null || end < nearest ? end : nearest;
    }, null);
    if (nextEnd === null) return;

    const delay = Math.max(50, nextEnd - (Date.now() + clockOffsetMs) + 150);
    const timeout = window.setTimeout(() => void refetch(), Math.min(delay, 2_147_000_000));
    return () => window.clearTimeout(timeout);
  }, [products, clockOffsetMs, refetch]);

  useEffect(() => {
    const giveaway = giveawayResponse?.giveaway;
    if (!giveaway) return;
    const delay = Math.max(50, Date.parse(giveaway.endsAt) - (Date.now() + giveawayClockOffsetMs) + 150);
    const timeout = window.setTimeout(() => void refetchGiveaway(), Math.min(delay, 2_147_000_000));
    return () => window.clearTimeout(timeout);
  }, [giveawayResponse?.giveaway, giveawayClockOffsetMs, refetchGiveaway]);

  useEffect(() => {
    const draw = flashDrawResponse?.draw;
    if (!draw) return;
    const targets = [draw.startsAt, draw.endsAt, draw.drawAt].filter((value): value is string => Boolean(value));
    const now = Date.now() + flashDrawClockOffsetMs;
    const next = targets.map(Date.parse).filter((time) => time > now).sort((a, b) => a - b)[0];
    if (!next) return;
    const timeout = window.setTimeout(
      () => void refetchFlashDraw(),
      Math.min(Math.max(100, next - now + 250), 2_147_000_000),
    );
    return () => window.clearTimeout(timeout);
  }, [flashDrawResponse?.draw, flashDrawClockOffsetMs, refetchFlashDraw]);

  // Stable identities, so the memoised cards do not all re-render whenever
  // this page does.
  const openProduct = useCallback((listing: ProductListing) => {
    setSearchOpen(false);
    setBuyNow(false);
    setSelectedVariantIds(listing.variants.map((variant) => variant.product.id));
    setSelectedId(listing.primary.id);
  }, []);

  const openListingConfirmation = useCallback((listing: ProductListing) => {
    setSearchOpen(false);
    setBuyNow(true);
    setSelectedVariantIds(listing.variants.map((variant) => variant.product.id));
    setSelectedId(listing.primary.id);
  }, []);

  const openConfirmation = useCallback((product: ProductDto) => {
    setSearchOpen(false);
    setBuyNow(true);
    setSelectedVariantIds([product.id]);
    setSelectedId(product.id);
  }, []);

  const closeSheet = useCallback(() => {
    setSelectedId(null);
    setSelectedVariantIds([]);
    // Stock changes after a purchase, so the grid is refreshed on close.
    void refetch();
  }, [refetch]);

  const renderCatalogResults = () => (
    <>
      {isLoading ? <Spinner label="Loading products" /> : null}

      {isError ? (
        <EmptyState
          icon={Package}
          title="Could not load products"
          description="Check your connection and try again."
        />
      ) : null}

      {!isLoading && !isError && visible.length === 0 ? (
        <EmptyState
          icon={Package}
          title={query ? 'No matches' : 'No products yet'}
          description={query ? 'Try a different search.' : 'Please check back soon.'}
        />
      ) : null}

      {visible.length > 0 ? (
        <ProductGrid
          products={visible}
          onSelect={openProduct}
          onBuy={openListingConfirmation}
          clockOffsetMs={clockOffsetMs}
        />
      ) : null}
    </>
  );

  return (
    <LayoutGroup id="store-search">
      <>
        <ConfettiCelebration
          runId={celebration.runId}
          onComplete={celebration.finish}
          preview={celebration.isPreview}
        />
        <Screen title="Shop Deals">
          {!searchOpen ? (
            <SearchField
              query={query}
              onChange={setQuery}
              onOpen={openSearch}
              layoutId="store-search-bar"
              placeholder="Search products and services"
              label="Search products"
            />
          ) : null}

          {!searchOpen ? (
            <StoreSectionSwitcher
              categories={categories}
              servicesOffered={servicesOffered}
              showingServices={showNumbers}
              categoryId={categoryId}
              onSelectServices={() => setShowNumbers(true)}
              onSelectCategory={(nextCategoryId) => {
                setShowNumbers(false);
                setCategoryId(nextCategoryId);
              }}
            />
          ) : null}

          {/* The heroes advertise products; the numbers tab sells none. */}
          {!showNumbers && flashDrawResponse?.draw ? (
            <FlashDrawHero
              draw={flashDrawResponse.draw}
              clockOffsetMs={flashDrawClockOffsetMs}
            />
          ) : null}

          {!showNumbers && giveawayResponse?.giveaway ? (
            <GiveawayHero
              giveaway={giveawayResponse.giveaway}
              clockOffsetMs={giveawayClockOffsetMs}
            />
          ) : null}

          {!showNumbers && featured ? (
            <FlashDealHero
              product={featured}
              clockOffsetMs={clockOffsetMs}
              onBuy={openConfirmation}
            />
          ) : null}

          {showNumbers ? <ServicesPage /> : renderCatalogResults()}
        </Screen>

        <StoreSearchOverlay
          open={searchOpen}
          query={query}
          onChange={setQuery}
          onClose={closeSearch}
        >
          {renderCatalogResults()}
        </StoreSearchOverlay>

        <ProductSheet
          product={selected}
          title={selectedListingName}
          variants={selectedVariants}
          onVariantSelect={setSelectedId}
          startConfirming={buyNow}
          clockOffsetMs={clockOffsetMs}
          onClose={closeSheet}
        />

        {!searchOpen && !selected ? <SupportFab /> : null}
      </>
    </LayoutGroup>
  );
}
